import React, { useContext, useEffect, useState } from 'react';
import { Table, Button, Badge, Space, Modal, Card, Row, Col, Input, Tag } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';

const { Search } = Input;

function Coupons() {
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const { doFetch: fetchCoupons } = useFetch();
  const { doFetch: deleteCouponFetch } = useFetch();

  useEffect(() => {
    setHeaderComponent('Coupon Management');

    return () => {
      setHeaderComponent(null);
    };
  }, [setHeaderComponent]);

  useEffect(() => {
    loadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const filtered = coupons.filter(coupon => {
        return (
          coupon.code?.toLowerCase().includes(query) ||
          coupon.description?.toLowerCase().includes(query)
        );
      });
      setFilteredCoupons(filtered);
    } else {
      setFilteredCoupons(coupons);
    }
  }, [searchQuery, coupons]);

  const loadCoupons = () => {
    fetchCoupons({
      url: URLS.getCoupons(),
      method: 'GET',
      onSuccess: (data) => {
        setCoupons(data.coupons || data || []);
        setFilteredCoupons(data.coupons || data || []);
      },
      onError: (error) => {
        console.error('Failed to load coupons:', error);
      },
    });
  };

  const handleDelete = (coupon) => {
    setSelectedCoupon(coupon);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedCoupon) {
      deleteCouponFetch({
        url: URLS.deleteCoupon({ couponId: selectedCoupon.id }),
        method: 'DELETE',
        onSuccess: () => {
          loadCoupons();
          setDeleteModalVisible(false);
          setSelectedCoupon(null);
        },
        showSuccessNotification: true,
        successMessage: 'Coupon deleted successfully',
      });
    }
  };


  const getStatusBadge = (coupon) => {
    if (!coupon.is_active) {
      return <Badge status="default" text="Inactive" />;
    }

    const now = dayjs();
    const validFrom = coupon.valid_from ? dayjs(coupon.valid_from) : null;
    const validUntil = coupon.valid_until ? dayjs(coupon.valid_until) : null;

    if (validUntil && now.isAfter(validUntil)) {
      return <Badge status="error" text="Expired" />;
    }

    if (validFrom && now.isBefore(validFrom)) {
      return <Badge status="warning" text="Scheduled" />;
    }

    return <Badge status="success" text="Active" />;
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Type',
      dataIndex: 'discount_type',
      key: 'discount_type',
      render: (type) => {
        const typeMap = {
          percentage: { color: 'blue', text: 'Percentage' },
          fixed: { color: 'green', text: 'Fixed Amount' },
        };
        const typeInfo = typeMap[type] || { color: 'default', text: type };
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
      },
    },
    {
      title: 'Discount',
      dataIndex: 'discount_value',
      key: 'discount_value',
      render: (value, record) => {
        if (record.discount_type === 'percentage') {
          return `${value}%`;
        }
        return `${value} ${record.currency || ''}`;
      },
    },
    {
      title: 'Valid From',
      dataIndex: 'valid_from',
      key: 'valid_from',
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : 'Immediate'),
    },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : 'No expiry'),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => {
        const usageCount = record.usage_count || 0;
        const maxUsage = record.max_usage || 'Unlimited';
        return `${usageCount} / ${maxUsage}`;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => getStatusBadge(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/coupons/${record.id}`)}
            size="small"
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} sm={12} md={16}>
            <Search
              placeholder="Search coupons by code or description"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/coupon-create')}
              size="large"
              block
            >
              Create New Coupon
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          dataSource={filteredCoupons}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} coupons`,
          }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Delete Coupon</span>
          </Space>
        }
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedCoupon(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete coupon <strong>{selectedCoupon?.code}</strong>?
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          This action cannot be undone. The coupon will be permanently removed.
        </p>
      </Modal>
    </div>
  );
}

export default Coupons;

